import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { createPublicLead } from '../lib/publicLead';

const { 
  FiPhone, FiMail, FiMapPin, FiClock, 
  FiCheckCircle, FiSend, FiUser, FiMessageSquare,
  FiAlertCircle
} = FiIcons;

function ContactSection() {
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    submitted: false,
    loading: false,
    error: null
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormState(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      await createPublicLead({
        name: formState.name,
        email: formState.email,
        phone: formState.phone,
        subject: formState.subject,
        message: formState.message,
        source: 'contact_form'
      });
      
      setFormState(prev => ({ 
        ...prev, 
        loading: false,
        submitted: true,
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      }));
    } catch (error) {
      console.error('Error submitting form:', error);
      setFormState(prev => ({ 
        ...prev, 
        loading: false,
        error: 'Παρουσιάστηκε σφάλμα κατά την αποστολή του μηνύματος. Παρακαλούμε δοκιμάστε ξανά.'
      }));
    }
  };

  const contactInfo = [
    {
      icon: FiPhone,
      title: 'Τηλέφωνο',
      content: '2821 821 269',
      link: 'tel:+302821821269'
    },
    {
      icon: FiMail,
      title: 'Email',
      content: 'info@codevaro.eu',
      link: 'mailto:info@codevaro.eu'
    },
    {
      icon: FiMapPin,
      title: 'Διεύθυνση',
      content: 'Χανιά, Κρήτη',
      link: 'https://goo.gl/maps/123'
    },
    {
      icon: FiClock,
      title: 'Ωράριο Λειτουργίας',
      content: 'Δευ-Παρ: 09:00-17:00',
      link: null
    }
  ];

  return (
    <section id="contact" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Επικοινωνήστε μαζί μας
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Είμαστε εδώ για να απαντήσουμε σε κάθε ερώτησή σας και να σας βοηθήσουμε να βρείτε την καλύτερη λύση για την επιχείρησή σας.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-5 gap-12">
          <motion.div
            className="lg:col-span-2"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="bg-white rounded-xl shadow-lg p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Στοιχεία Επικοινωνίας</h3>
              <div className="space-y-6">
                {contactInfo.map((item, index) => (
                  <motion.div 
                    key={index}
                    className="flex items-start"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <div className="bg-blue-100 p-3 rounded-lg mr-4">
                      <SafeIcon icon={item.icon} className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">{item.title}</h4>
                      {item.link ? (
                        <a 
                          href={item.link} 
                          className="text-blue-600 hover:underline"
                          target={item.link.startsWith('http') ? '_blank' : '_self'}
                          rel="noopener noreferrer"
                        >
                          {item.content}
                        </a>
                      ) : (
                        <p className="text-gray-600">{item.content}</p>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
              <div className="mt-8 pt-8 border-t border-gray-200">
                <h4 className="font-semibold text-gray-900 mb-4">Ακολουθήστε μας</h4>
                <div className="flex space-x-4">
                  {['facebook', 'instagram', 'linkedin', 'twitter'].map((platform) => (
                    <a
                      key={platform}
                      href={`https://${platform}.com/codevaro`}
                      className="bg-gray-100 hover:bg-blue-100 p-3 rounded-full transition-colors"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <SafeIcon icon={`Fi${platform.charAt(0).toUpperCase() + platform.slice(1)}`} className="w-5 h-5 text-gray-700" />
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="lg:col-span-3"
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="bg-white rounded-xl shadow-lg p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Στείλτε μας μήνυμα</h3>
              {formState.error && (
                <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4 flex items-start">
                  <SafeIcon icon={FiAlertCircle} className="w-5 h-5 text-red-500 mr-3 mt-0.5" />
                  <p className="text-red-700">{formState.error}</p>
                </div>
              )}
              {formState.submitted ? (
                <motion.div 
                  className="text-center py-12"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5 }}
                >
                  <div className="bg-green-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                    <SafeIcon icon={FiCheckCircle} className="w-10 h-10 text-green-600" />
                  </div>
                  <h4 className="text-2xl font-bold text-gray-900 mb-2">Ευχαριστούμε!</h4>
                  <p className="text-gray-600 mb-6">
                    Το μήνυμά σας έχει σταλεί με επιτυχία. Θα επικοινωνήσουμε μαζί σας σύντομα.
                  </p>
                  <button
                    onClick={() => setFormState(prev => ({ ...prev, submitted: false }))}
                    className="text-blue-600 font-medium hover:underline"
                  >
                    Στείλτε νέο μήνυμα
                  </button>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">Ονοματεπώνυμο</label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><SafeIcon icon={FiUser} className="w-5 h-5 text-gray-400" /></div>
                        <input type="text" id="name" name="name" value={formState.name} onChange={handleChange} className="pl-10 w-full rounded-lg border border-gray-300 p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors" placeholder="Το όνομά σας" required />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><SafeIcon icon={FiMail} className="w-5 h-5 text-gray-400" /></div>
                        <input type="email" id="email" name="email" value={formState.email} onChange={handleChange} className="pl-10 w-full rounded-lg border border-gray-300 p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors" placeholder="Το email σας" required />
                      </div>
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">Τηλέφωνο</label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"><SafeIcon icon={FiPhone} className="w-5 h-5 text-gray-400" /></div>
                        <input type="tel" id="phone" name="phone" value={formState.phone} onChange={handleChange} className="pl-10 w-full rounded-lg border border-gray-300 p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors" placeholder="Το τηλέφωνό σας" />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">Θέμα</label>
                      <select id="subject" name="subject" value={formState.subject} onChange={handleChange} className="w-full rounded-lg border border-gray-300 p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors" required>
                        <option value="">Επιλέξτε θέμα</option><option value="general">Γενική Πληροφόρηση</option><option value="tech">Τεχνική Υποστήριξη</option><option value="web">Web Services</option><option value="marketing">Marketing & SEO</option><option value="ecommerce">E-Commerce</option><option value="other">Άλλο</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">Μήνυμα</label>
                    <div className="relative">
                      <div className="absolute top-3 left-3 pointer-events-none"><SafeIcon icon={FiMessageSquare} className="w-5 h-5 text-gray-400" /></div>
                      <textarea id="message" name="message" value={formState.message} onChange={handleChange} rows={5} className="pl-10 w-full rounded-lg border border-gray-300 p-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors" placeholder="Πώς μπορούμε να σας βοηθήσουμε;" required />
                    </div>
                  </div>
                  <div className="flex items-center">
                    <input id="privacy" type="checkbox" required className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                    <label htmlFor="privacy" className="ml-2 text-sm text-gray-600">Συμφωνώ με την <a href="#" className="text-blue-600 hover:underline">Πολιτική Απορρήτου</a></label>
                  </div>
                  <motion.button type="submit" className="w-full bg-blue-600 text-white px-6 py-4 rounded-lg hover:bg-blue-700 transition-colors font-semibold flex items-center justify-center" whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} disabled={formState.loading}>
                    {formState.loading ? <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white mr-2"></div> : <SafeIcon icon={FiSend} className="mr-2 w-5 h-5" />}
                    {formState.loading ? 'Αποστολή...' : 'Αποστολή Μηνύματος'}
                  </motion.button>
                </form>
              )}
            </div>
          </motion.div>
        </div>

        <motion.div className="mt-16 rounded-xl overflow-hidden shadow-lg h-96 bg-gray-200" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }} viewport={{ once: true }}>
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-r from-blue-50 to-blue-100">
            <div className="text-center">
              <SafeIcon icon={FiMapPin} className="w-12 h-12 text-blue-500 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900">Χανιά, Κρήτη</h3>
              <p className="text-gray-600">Επισκεφθείτε μας στα γραφεία μας</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

export default ContactSection;